import { useCallback, useEffect, useState } from "react";
import { api } from "../../api.js";
import GoogleSignIn from "../../components/GoogleSignIn.jsx";
import { Button, Eyebrow, Field, Notice } from "../../components/ui.jsx";
import { ActionLink, PublicHeader } from "./PublicChrome.jsx";

export default function LoginPage({ onAccount }) {
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get("email") || "");
  const [password, setPassword] = useState("");
  const [googleCredential, setGoogleCredential] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inviteToken = new URLSearchParams(window.location.search).get("invite") || null;
  useEffect(() => { document.title = "Sign in · RevPal"; }, []);

  const googleError = useCallback((requestError) => {
    setError(requestError.message);
  }, []);

  const continueWithGoogle = useCallback(async (credential) => {
    setError("");
    setBusy(true);
    try {
      const data = await api.googleSignIn(credential, { inviteToken });
      onAccount(data.account);
    } catch (requestError) {
      if (requestError.code === "google_link_confirmation_required") {
        setGoogleCredential(credential);
        setPassword("");
      } else {
        setError(requestError.message);
      }
    } finally {
      setBusy(false);
    }
  }, [inviteToken, onAccount]);

  async function submit(event) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = googleCredential
        ? await api.googleSignIn(googleCredential, { password, inviteToken })
        : await api.login(email, password);
      onAccount(data.account);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicHeader current="login" />
      <main className="mx-auto grid min-h-[calc(100vh-80px)] w-full max-w-[1440px] lg:grid-cols-[minmax(320px,.88fr)_minmax(460px,1.12fr)]">
        <section className="flex flex-col justify-between border-b border-border bg-background p-8 lg:border-b-0 lg:border-r lg:p-[clamp(40px,6vw,88px)]">
          <div className="grid max-w-[580px] gap-6 py-12"><Eyebrow>Return to the record</Eyebrow><h1 className="max-w-[14ch] text-[clamp(2.5rem,4.2vw,3.75rem)] font-extrabold leading-[1.04] tracking-[-0.035em]">Pick up with the full history.</h1><p className="max-w-[48ch] text-sm leading-7 text-muted-foreground">Your companies, relationships, evidence, agent activity, and pending approvals are waiting in one workspace.</p></div>
          <span className="text-xs text-muted-foreground">Session access is workspace-scoped and expires automatically.</span>
        </section>
        <section className="grid place-items-center px-5 py-16 sm:px-8">
          <form className="grid w-full max-w-[440px] gap-6 rounded-xl bg-card p-8 shadow-soft" onSubmit={submit}>
            <div className="grid gap-3"><Eyebrow>Welcome back</Eyebrow><h2 className="text-[30px] font-bold tracking-[-0.02em]">Sign in to RevPal</h2><p className="text-sm text-muted-foreground">New here? <a className="font-bold text-primary hover:text-primary-600" href="/signup">Create an account</a>.</p></div>
            {!googleCredential && <GoogleSignIn onCredential={continueWithGoogle} onError={googleError} />}
            {error && <Notice>{error}</Notice>}
            {googleCredential ? (
              <div className="grid gap-5 rounded-xl border border-border bg-muted p-5">
                <div className="grid gap-2"><Eyebrow>One-time account check</Eyebrow><p className="text-sm leading-6 text-muted-foreground">This email already has a RevPal login. Enter its current password once to link Google securely.</p></div>
                <Field label="Current RevPal password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" autoFocus required />
                <div className="flex flex-wrap justify-end gap-2"><Button type="button" onClick={() => { setGoogleCredential(""); setPassword(""); setError(""); }}>Cancel</Button><Button variant="primary" type="submit" disabled={busy}>{busy ? "Linking…" : "Link Google account"}</Button></div>
              </div>
            ) : (
              <>
                <Field label="Work email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" autoFocus required />
                <Field label="Password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required />
                <Button variant="primary" type="submit" className="w-full" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</Button>
              </>
            )}
            <ActionLink href="/" variant="ghost" className="justify-self-center">← Back to homepage</ActionLink>
          </form>
        </section>
      </main>
    </div>
  );
}
